// ---------------------------------------------------------------------------
// Ontology Context — Builds LLM-friendly system prompt from AppOntology.
//
// Used by enrichAgentWithOntology() to inject app understanding into
// GeminiAgentRuntime's system_instruction.
// ---------------------------------------------------------------------------

import type { AppOntology } from './types'

export interface OntologySummary {
  datasourceCount: number
  flowCount: number
  stateCount: number
  uiBindingCount: number
  edgeCount: number
  topFlows: string[]  // flow names sorted by connectivity (most edges first)
}

export interface OntologyPromptOptions {
  maxFlows?: number        // default 20
  maxDatasources?: number  // default 15
  maxStates?: number       // default 30
  maxBindings?: number     // default 20
}

/**
 * Build a compact summary object from AppOntology.
 * Useful for dashboards, logging, quick inspection.
 */
export function buildOntologySummary(ontology: AppOntology): OntologySummary {
  // Count edges per flow to sort by connectivity
  const flowEdgeCounts = new Map<string, number>()
  for (const flow of ontology.flows) {
    flowEdgeCounts.set(flow.id, 0)
  }
  for (const edge of ontology.edges) {
    if (flowEdgeCounts.has(edge.source)) {
      flowEdgeCounts.set(edge.source, (flowEdgeCounts.get(edge.source) ?? 0) + 1)
    }
    if (flowEdgeCounts.has(edge.target)) {
      flowEdgeCounts.set(edge.target, (flowEdgeCounts.get(edge.target) ?? 0) + 1)
    }
  }

  const topFlows = [...ontology.flows]
    .sort((a, b) => (flowEdgeCounts.get(b.id) ?? 0) - (flowEdgeCounts.get(a.id) ?? 0))
    .slice(0, 10)
    .map(f => `${f.blockName}.${f.flowName}`)

  return {
    datasourceCount: ontology.datasources.length,
    flowCount: ontology.flows.length,
    stateCount: ontology.states.length,
    uiBindingCount: ontology.uiBindings.length,
    edgeCount: ontology.edges.length,
    topFlows,
  }
}

/**
 * Build a human-readable markdown text summary of AppOntology for LLM system context.
 *
 * Includes: datasource URLs, flow triggers+actions, state read/write graph, UI bindings.
 * Large ontologies are truncated to stay within token limits.
 */
export function buildOntologySystemPrompt(
  ontology: AppOntology,
  options: OntologyPromptOptions = {},
): string {
  const {
    maxFlows = 20,
    maxDatasources = 15,
    maxStates = 30,
    maxBindings = 20,
  } = options

  const sections: string[] = []

  // Header with counts
  sections.push('## App Data Flow')
  sections.push(
    `Datasources: ${ontology.datasources.length} | Flows: ${ontology.flows.length} | States: ${ontology.states.length} | UI Bindings: ${ontology.uiBindings.length}`,
  )

  // Datasources
  if (ontology.datasources.length > 0) {
    sections.push('\n### Datasources')
    const shown = ontology.datasources.slice(0, maxDatasources)
    for (const ds of shown) {
      sections.push(
        `- [${ds.blockName}] ${ds.method} ${ds.url}${ds.resultTarget ? ` -> ${ds.resultTarget}` : ''}`,
      )
    }
    if (ontology.datasources.length > maxDatasources) {
      sections.push(`... and ${ontology.datasources.length - maxDatasources} more datasources`)
    }
  }

  // Flows
  if (ontology.flows.length > 0) {
    sections.push('\n### Flows')
    const shown = ontology.flows.slice(0, maxFlows)
    for (const flow of shown) {
      sections.push(`- ${flow.blockName}.${flow.flowName} [${flow.trigger}]: ${flow.actionSummary}`)
    }
    if (ontology.flows.length > maxFlows) {
      sections.push(`... and ${ontology.flows.length - maxFlows} more flows`)
    }
  }

  // State Graph
  if (ontology.states.length > 0) {
    sections.push('\n### State Graph')
    const shown = ontology.states.slice(0, maxStates)
    for (const state of shown) {
      const writers = state.writtenByFlows.map(id => id.split(':').pop()).join(', ')
      const readers = state.readByFlows.map(id => id.split(':').pop()).join(', ')
      sections.push(
        `- ${state.key} (${state.scope}) written by: [${writers}], read by: [${readers}]`,
      )
    }
    if (ontology.states.length > maxStates) {
      sections.push(`... and ${ontology.states.length - maxStates} more states`)
    }
  }

  // UI Bindings
  if (ontology.uiBindings.length > 0) {
    sections.push('\n### UI Bindings')
    const shown = ontology.uiBindings.slice(0, maxBindings)
    for (const binding of shown) {
      sections.push(`- ${binding.blockName}:${binding.elementId} -> #{${binding.boundVariable}}`)
    }
    if (ontology.uiBindings.length > maxBindings) {
      sections.push(`... and ${ontology.uiBindings.length - maxBindings} more bindings`)
    }
  }

  return sections.join('\n')
}
